import { DeleteOutlined } from "@mui/icons-material";
import { Box, InputAdornment, TableCell, TableRow, TextField, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useRoleColors } from "../../../hooks/useRoleColors";
import { normalizeSelectedJobIds, selectedJobIdsForDisplay } from "../../../utils/jobSelection";
import { EmploymentType, type Job, NurseFullRole, type NurseRole, NurseTrainingStatus, RateHoursBonusType } from "../../../utils/models";
import { nurseRoleDisplayName } from "../../../utils/utilFunctions";
import { type FlatSwitchOption, SystemFlatSwitch } from "../../UIToolkit/Clickable/SystemFlatSwitch";
import { SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { ConfigMultiSelectCell } from "../../UIToolkit/Tables/ConfigTable/ConfigMultiSelectCell";
import { BORDERLESS_INPUT_SX, BORDERLESS_SELECT_SX, CONFIG_TABLE_BODY_CELL_SX, configTableBodyRowSx } from "../../UIToolkit/Tables/ConfigTable/configTableSx";
import { ConfigUnionCell } from "./ConfigUnionCell";
import { type HoursRateDraftRow, HoursRateTimeCell } from "./HoursRateTimeCell";

/** The tri-state cells' own values. Not persisted: they map onto `isCharge` / `weekend` (undefined / true / false). */
const TRI_STATE_ALL = "all";
const CHARGE_YES = "yes";
const CHARGE_NO = "no";
const WEEKDAY = "weekday";
const WEEKEND = "weekend";

const ALL_TRAINING_STATUSES = Object.values(NurseTrainingStatus);
const ALL_EMPLOYMENT_TYPES = Object.values(EmploymentType);

const CAPTION_SX = { color: "error.main", mt: 0.5, display: "block" } as const;

/** Undefined = "not chosen" (red caption), [] = "all", subset = specific, from a raw multi-select change. */
const collapseSelection = <T,>(selected: T[], fullSet: readonly T[]): T[] | undefined => {
	// Nothing to choose from (e.g. a job-managed department with no roles): the only possible pick is "all".
	if (fullSet.length === 0) return [];
	if (selected.length === 0) return undefined;
	const isAll = selected.length === fullSet.length && fullSet.every((value) => selected.includes(value));
	return isAll ? [] : selected;
};

/** The inverse, for display: [] shows every option checked, undefined shows none. */
const expandSelection = <T,>(stored: T[] | null | undefined, fullSet: readonly T[]): T[] => {
	if (stored == null) return [];
	return stored.length === 0 ? [...fullSet] : stored;
};

type Props = {
	row: HoursRateDraftRow;
	index: number;
	roles: NurseRole[];
	departmentJobs: Job[];
	showJobColumn: boolean;
	agencies: string[];
	existingUnions: string[] | undefined;
	bonusTypeOptions: FlatSwitchOption<RateHoursBonusType>[];
	updateRow: (index: number, update: (row: HoursRateDraftRow) => HoursRateDraftRow) => void;
	removeRow: (index: number) => void;
	disabled: boolean;
	/** The rule a deep link pointed at: briefly washed and marked `data-highlighted="true"`. */
	highlighted: boolean;
};

/** One hours-rate (wage differential) rule. Every edit goes through `updateRow`, against the latest list. */
export const HoursRateConfigRow: FC<Props> = ({
	row,
	index,
	roles,
	departmentJobs,
	showJobColumn,
	agencies,
	existingUnions,
	bonusTypeOptions,
	updateRow,
	removeRow,
	disabled,
	highlighted,
}) => {
	const roleColor = useRoleColors();
	const { t } = useTranslation();
	const patch = (changes: Partial<HoursRateDraftRow>) => updateRow(index, (current) => ({ ...current, ...changes }));

	return (
		<TableRow
			sx={configTableBodyRowSx(highlighted)}
			data-testid={`dept-config-rates-hours-rate-row-${index}`}
			data-highlighted={highlighted ? "true" : undefined}
		>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				<TextField
					size="small"
					fullWidth
					disabled={disabled}
					sx={BORDERLESS_INPUT_SX}
					value={row.name}
					onChange={(e) => patch({ name: e.target.value })}
					slotProps={{ htmlInput: { "data-testid": `dept-config-rates-hours-rate-name-${index}` } }}
				/>
			</TableCell>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				<HoursRateTimeCell row={row} updateRow={(update) => updateRow(index, update)} disabled={disabled} />
			</TableCell>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				<ConfigMultiSelectCell<NurseRole>
					id={`hours-rate-roles-${index}`}
					disabled={disabled}
					allOption={t("department.config.rates.option.all")}
					items={roles.map((role) => ({
						value: role,
						label: NurseFullRole[role],
						selectedLabel: nurseRoleDisplayName[role] ?? role,
						color: roleColor(NurseFullRole[role]).dot,
					}))}
					selectedItems={expandSelection(row.roles, roles)}
					onChangeMultiple={(selectedValues) => patch({ roles: collapseSelection(selectedValues, roles) })}
					errorText={row.roles === undefined ? t("department.config.rates.chooseAllOrRoles") : undefined}
				/>
			</TableCell>
			{showJobColumn && (
				<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
					<ConfigMultiSelectCell<string>
						id={`hours-rate-jobs-${index}`}
						disabled={disabled}
						allOption={t("department.config.rates.option.all")}
						items={departmentJobs.map((j) => ({ value: j.id, label: j.displayName ?? j.name, selectedLabel: j.displayName ?? j.name }))}
						selectedItems={selectedJobIdsForDisplay(row.jobIds, departmentJobs)}
						onChangeMultiple={(selectedValues) => patch({ jobIds: normalizeSelectedJobIds(selectedValues, departmentJobs) })}
					/>
				</TableCell>
			)}
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				<TextField
					type="number"
					size="small"
					fullWidth
					disabled={disabled}
					sx={BORDERLESS_INPUT_SX}
					value={row.bonusVal ?? ""}
					onChange={(e) => patch({ bonusVal: e.target.value === "" ? 0 : Number(e.target.value) })}
					slotProps={{
						input: { startAdornment: <InputAdornment position="start">{row.bonusType === RateHoursBonusType.Percentage ? "%" : "$"}</InputAdornment> },
						htmlInput: { min: 0, step: 0.01, style: { textAlign: "center" }, "data-testid": `dept-config-rates-hours-rate-bonus-${index}` },
					}}
				/>
			</TableCell>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				<Box sx={{ minWidth: 160, maxWidth: 200 }}>
					<SystemFlatSwitch<RateHoursBonusType>
						options={bonusTypeOptions}
						activeIndexOrValue={row.bonusType ?? RateHoursBonusType.FlatRate}
						onSwitch={(option) => patch({ bonusType: option.value })}
						delayCallback={0}
						rootWidth="100%"
						data-testid={`dept-config-rates-hours-rate-bonus-type-${index}`}
					/>
				</Box>
			</TableCell>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				<SystemDropdown<string>
					formControlSx={{ width: "100%" }}
					selectSx={BORDERLESS_SELECT_SX}
					id={`hours-rate-charge-${index}`}
					fullWidth
					disabled={disabled}
					items={[
						{ value: TRI_STATE_ALL, label: t("department.config.rates.option.all") },
						{ value: CHARGE_YES, label: t("department.config.rates.option.yes") },
						{ value: CHARGE_NO, label: t("department.config.rates.option.no") },
					]}
					selectedItem={row.isCharge === true ? CHARGE_YES : row.isCharge === false ? CHARGE_NO : TRI_STATE_ALL}
					onChange={(value) => patch({ isCharge: value === undefined || value === TRI_STATE_ALL ? undefined : value === CHARGE_YES })}
				/>
			</TableCell>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				<SystemDropdown<NurseTrainingStatus>
					formControlSx={{ width: "100%" }}
					selectSx={BORDERLESS_SELECT_SX}
					id={`hours-rate-training-status-${index}`}
					fullWidth
					multiple
					disabled={disabled}
					allOption={t("department.config.rates.option.all")}
					items={ALL_TRAINING_STATUSES.map((status) => ({ value: status, label: status, selectedLabel: status }))}
					selectedItems={expandSelection(row.trainingStatuses, ALL_TRAINING_STATUSES)}
					onChangeMultiple={(selectedValues) => patch({ trainingStatuses: collapseSelection(selectedValues, ALL_TRAINING_STATUSES) })}
				/>
				{row.trainingStatuses === undefined && (
					<Typography variant="caption" sx={CAPTION_SX}>
						{t("department.config.rates.chooseAllOrTrainingStatuses")}
					</Typography>
				)}
			</TableCell>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				<ConfigMultiSelectCell<EmploymentType>
					id={`hours-rate-employment-type-${index}`}
					disabled={disabled}
					allOption={t("department.config.rates.option.all")}
					items={ALL_EMPLOYMENT_TYPES.map((et) => ({ value: et, label: et, selectedLabel: et }))}
					selectedItems={expandSelection(row.employmentTypes, ALL_EMPLOYMENT_TYPES)}
					onChangeMultiple={(selectedValues) => patch({ employmentTypes: collapseSelection(selectedValues, ALL_EMPLOYMENT_TYPES) })}
					errorText={row.employmentTypes === undefined ? t("department.config.rates.chooseAllOrEmploymentTypes") : undefined}
				/>
			</TableCell>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				<SystemDropdown<string>
					formControlSx={{ width: "100%" }}
					selectSx={BORDERLESS_SELECT_SX}
					id={`hours-rate-weekend-${index}`}
					fullWidth
					disabled={disabled}
					items={[
						{ value: TRI_STATE_ALL, label: t("department.config.rates.option.all") },
						{ value: WEEKDAY, label: t("department.config.rates.option.weekday") },
						{ value: WEEKEND, label: t("department.config.rates.option.weekend") },
					]}
					selectedItem={row.weekend === true ? WEEKEND : row.weekend === false ? WEEKDAY : TRI_STATE_ALL}
					onChange={(value) => patch({ weekend: value === undefined || value === TRI_STATE_ALL ? undefined : value === WEEKEND })}
				/>
			</TableCell>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				<ConfigUnionCell
					ariaLabel={t("department.config.rates.hoursRate.col.union")}
					dataTestId={`dept-config-rates-hours-rate-union-${index}`}
					isUnion={row.isUnion}
					union={row.union}
					existingUnions={existingUnions}
					disabled={disabled}
					onChange={patch}
				/>
			</TableCell>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				<SystemDropdown<string>
					formControlSx={{ width: "100%" }}
					selectSx={BORDERLESS_SELECT_SX}
					id={`hours-rate-agency-${index}`}
					fullWidth
					multiple
					allOption={t("department.config.rates.option.all")}
					disabled={disabled || agencies.length === 0}
					items={agencies.map((agency) => ({ value: agency, label: agency, selectedLabel: agency }))}
					selectedItems={row.agency === undefined ? [] : row.agency.length === 0 ? agencies : row.agency}
					onChangeMultiple={(selectedValues) => patch({ agency: collapseSelection(selectedValues, agencies) })}
					MenuProps={{ sx: { "& .MuiMenuItem-root": { textTransform: "none" } } }}
				/>
			</TableCell>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				<SystemButton
					onClick={() => removeRow(index)}
					buttonVariant="borderless"
					size="small"
					disabled={disabled}
					leadingIcon={<DeleteOutlined fontSize="small" />}
					tooltip={t("department.config.rates.delete")}
					aria-label={t("department.config.rates.delete")}
					data-testid={`dept-config-rates-hours-rate-delete-${index}`}
				/>
			</TableCell>
		</TableRow>
	);
};
